// ─────────────────────────────────────────────
//  Sandbox Firestore shim — the nineteen primitives firebase/firestore.js
//  imports, over a local store.
//
//  WHY A SHIM AND NOT A REWRITE: firestore.js is 1177 lines, 111 exported
//  helpers and 63 consumers — but it only imports NINETEEN SDK primitives.
//  Implementing those nineteen means every helper and every consumer works
//  unmodified. Reimplementing 111 functions would not.
//
//  Every reference this module hands out is tagged. A reference from the real
//  SDK, or from a different store, throws immediately and by name — silently
//  mixing real and local data is the worst failure available here, so it is
//  made loud.
// ─────────────────────────────────────────────
import { createStore } from "./localStore";
import {
  applyConstraints, whereConstraint, orderByConstraint, limitConstraint,
} from "./localQuery";

const DB_TAG = "__atcSandboxDb";
const REF_TAG = "__atcSandboxRef";

// ── Timestamp ────────────────────────────────
class LocalTimestamp {
  constructor(ms) { this._ms = ms; }
  toMillis() { return this._ms; }
  toDate() { return new Date(this._ms); }
  get seconds() { return Math.floor(this._ms / 1000); }
  get nanoseconds() { return (this._ms % 1000) * 1e6; }
  isEqual(o) { return !!o && typeof o.toMillis === "function" && o.toMillis() === this._ms; }
}
export const Timestamp = {
  now: () => new LocalTimestamp(Date.now()),
  fromDate: (d) => new LocalTimestamp(d.getTime()),
  fromMillis: (ms) => new LocalTimestamp(ms),
};

// ── Timestamp (de)hydration ──────────────────
//  The store deep-copies with structuredClone, which keeps own properties but
//  DROPS the prototype — a LocalTimestamp put in comes back out as a bare
//  { _ms } with no toMillis. IndexedDB does the same across a reload. That is
//  a silent failure of the worst kind: firestore.js calls .toDate() on
//  timestamps everywhere, and localQuery sorts them by `typeof v.toMillis ===
//  "function"`, so a stripped timestamp makes orderBy quietly stop ordering.
//
//  So timestamps are stored as a plain, structuredClone-safe marker and revived
//  into a Timestamp on every read. Dates are converted on the way in too,
//  matching real Firestore, which stores a Date and returns a Timestamp.
const TS_MARKER = "__atcTs";
const tsMarker = (ms) => ({ [TS_MARKER]: ms });
const isTsMarker = (v) => !!v && typeof v === "object" && typeof v[TS_MARKER] === "number";
const isTsLike = (v) => !!v && typeof v === "object" && typeof v.toMillis === "function";

const dehydrate = (v) => {
  if (Array.isArray(v)) return v.map(dehydrate);
  if (v instanceof Date) return tsMarker(v.getTime());
  if (isTsLike(v)) return tsMarker(v.toMillis());
  if (v && typeof v === "object" && !isTsMarker(v)) {
    const out = {};
    for (const [k, x] of Object.entries(v)) out[k] = dehydrate(x);
    return out;
  }
  return v;
};

const hydrate = (v) => {
  if (Array.isArray(v)) return v.map(hydrate);
  if (isTsMarker(v)) return new LocalTimestamp(v[TS_MARKER]);
  if (v && typeof v === "object") {
    const out = {};
    for (const [k, x] of Object.entries(v)) out[k] = hydrate(x);
    return out;
  }
  return v;
};

// ── Field sentinels ──────────────────────────
const SENTINEL = "__atcSentinel";
export const serverTimestamp = () => ({ [SENTINEL]: "serverTimestamp" });
export const arrayUnion = (...values) => ({ [SENTINEL]: "arrayUnion", values });
export const arrayRemove = (...values) => ({ [SENTINEL]: "arrayRemove", values });
export const deleteField = () => ({ [SENTINEL]: "deleteField" });

const isSentinel = (v) => !!v && typeof v === "object" && SENTINEL in v;
const sameValue = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Resolves sentinels against the document's CURRENT value. Returns the new
// document data; keys marked deleteField are dropped.
//
// Called EXACTLY ONCE per operation. Batch and transaction queues store the
// caller's raw patch and resolve here at apply time — resolving again while
// queueing would append an arrayUnion twice and re-stamp serverTimestamp,
// both without any error.
//
// Works entirely in stored (dehydrated) space, so arrayUnion's identity
// comparison sees marker-vs-marker rather than marker-vs-Timestamp.
// FIELD PATHS. updateDoc() treats a dotted key as a path INTO A NESTED MAP —
// { "onboarding.orgName": "X" } sets next.onboarding.orgName, it does not
// create a top-level key literally named "onboarding.orgName". setDoc() does
// NOT do this; there a dotted key is just a key. Honouring it only on the
// update path is deliberate, not an oversight.
//
// This was found live, not in a unit test: the onboarding wizard wrote
// "onboarding.orgName", the shim stored it flat, so `user.onboarding` stayed
// undefined and the wizard sat on step one forever with no error anywhere.
// Three PRE-EXISTING call sites in firestore.js do the same thing
// (`locumLeaveTimes.${code}`, `departed.${memberId}`), so locum leave times
// and departures would have silently written garbage too.
const readPath = (obj, segments) =>
  segments.reduce((acc, s) => (acc == null ? undefined : acc[s]), obj);

// Writes value at a dotted path, cloning each level so the stored document is
// never mutated in place. `undefined` deletes the leaf (deleteField).
const writePath = (root, segments, value) => {
  const [head, ...rest] = segments;
  const branch = rest.length
    ? writePath(
        root && typeof root[head] === "object" && root[head] !== null ? root[head] : {},
        rest,
        value
      )
    : value;
  const next = { ...(root || {}) };
  if (rest.length === 0 && value === undefined) delete next[head];
  else next[head] = branch;
  return next;
};

const resolveWrite = (current, rawPatch, { expandPaths = false } = {}) => {
  const patch = dehydrate(rawPatch);
  let next = { ...(current || {}) };

  const put = (key, value) => {
    if (!expandPaths || !key.includes(".")) {
      if (value === undefined) delete next[key];
      else next[key] = value;
      return;
    }
    next = writePath(next, key.split("."), value);
  };
  const peek = (key) =>
    !expandPaths || !key.includes(".") ? next[key] : readPath(next, key.split("."));

  for (const [key, value] of Object.entries(patch)) {
    if (!isSentinel(value)) { put(key, value); continue; }
    switch (value[SENTINEL]) {
      case "serverTimestamp":
        put(key, tsMarker(Date.now()));
        break;
      case "deleteField":
        put(key, undefined);
        break;
      case "arrayUnion": {
        const existing = peek(key);
        const base = Array.isArray(existing) ? [...existing] : [];
        for (const v of value.values) {
          if (!base.some((x) => sameValue(x, v))) base.push(v);
        }
        put(key, base);
        break;
      }
      case "arrayRemove": {
        const existing = peek(key);
        const base = Array.isArray(existing) ? existing : [];
        put(key, base.filter((x) => !value.values.some((v) => sameValue(x, v))));
        break;
      }
      default:
        throw new Error(`[sandbox] unknown field sentinel: ${value[SENTINEL]}`);
    }
  }
  return next;
};

// ── Database + references ────────────────────
let seq = 0;
const newId = () => `sbx${(++seq).toString(36)}${Math.random().toString(36).slice(2, 8)}`;

export function createLocalDb(options = {}) {
  const store = createStore(options);
  return {
    [DB_TAG]: true,
    store,
    ready: store.ready,
    // ⚠️ `ready` is HYDRATION. `flushNow` is the one that guarantees writes are
    // on disk — a caller that fills the store and reloads needs the second, not
    // the first.
    flushNow: () => store.flushNow(),
  };
}

export { deleteStoredSnapshot } from "./localStore";

const assertDb = (db) => {
  if (!db || !db[DB_TAG]) {
    throw new Error(
      "[sandbox] that is not a sandbox database. A real Firestore instance " +
      "reached the sandbox shim — check the boot-time db selection in config.js."
    );
  }
  return db;
};

const assertRef = (ref) => {
  if (!ref || !ref[REF_TAG]) {
    throw new Error(
      "[sandbox] that is not a sandbox reference. A real Firestore reference " +
      "reached the sandbox shim — real and local data must never mix."
    );
  }
  return ref;
};

export function collection(db, ...segments) {
  assertDb(db);
  return { [REF_TAG]: true, kind: "collection", db, path: segments.join("/") };
}

export function doc(dbOrCol, ...segments) {
  // doc(db, "rooms", "r1") and doc(collectionRef, "r1") are both used.
  if (dbOrCol && dbOrCol[REF_TAG] && dbOrCol.kind === "collection") {
    const id = segments[0] ?? newId();
    return { [REF_TAG]: true, kind: "document", db: dbOrCol.db, id, path: `${dbOrCol.path}/${id}` };
  }
  assertDb(dbOrCol);
  const path = segments.join("/");
  const id = path.split("/").pop();
  return { [REF_TAG]: true, kind: "document", db: dbOrCol, id, path };
}

export const where = whereConstraint;
export const orderBy = orderByConstraint;
export const limit = limitConstraint;

export function query(colRef, ...constraints) {
  assertRef(colRef);
  return { ...colRef, kind: "query", constraints: [...(colRef.constraints || []), ...constraints] };
}

// ── Snapshots ────────────────────────────────
// ⚠️ `metadata` IS PART OF THE SNAPSHOT CONTRACT. `useLiveConnection` reads
// `snap.metadata.fromCache` to decide whether the board says LIVE or
// RECONNECTING; without it the sandbox board would sit on RECONNECTING forever,
// a permanent false alarm on a store that cannot be stale.
//
// fromCache: false is the honest answer here. The local store is not a cache OF
// anything — it is the source of truth for this session, always current, with
// no pending write waiting on a server.
const LOCAL_METADATA = { fromCache: false, hasPendingWrites: false };

const docSnapshot = (ref, data) => ({
  id: ref.id,
  ref,
  metadata: LOCAL_METADATA,
  exists: () => data !== undefined,
  data: () => (data === undefined ? undefined : data),
  get: (field) => (data ? field.split(".").reduce((a, k) => (a == null ? undefined : a[k]), data) : undefined),
});

const collectionPathOf = (ref) => ref.path.split("/")[0];

// Result documents carry a FULL reference, database included. firestore.js
// does `snap.docs.map((d) => deleteDoc(d.ref))` in five places; a ref without
// its database loses the store and throws on the first write.
const rowRef = (source, id) => ({
  [REF_TAG]: true,
  kind: "document",
  db: source.db,
  id,
  path: `${collectionPathOf(source)}/${id}`,
});

const querySnapshot = (source, rows, previousIds) => {
  const docs = rows.map((r) => docSnapshot(rowRef(source, r.id), r.data));
  const currentIds = new Set(rows.map((r) => r.id));
  return {
    docs,
    size: docs.length,
    empty: docs.length === 0,
    forEach: (fn) => docs.forEach(fn),
    docChanges: () => {
      if (!previousIds) return docs.map((d) => ({ type: "added", doc: d }));
      const added = docs.filter((d) => !previousIds.has(d.id)).map((d) => ({ type: "added", doc: d }));
      const modified = docs.filter((d) => previousIds.has(d.id)).map((d) => ({ type: "modified", doc: d }));
      const removed = [...previousIds].filter((id) => !currentIds.has(id))
        .map((id) => ({ type: "removed", doc: docSnapshot(rowRef(source, id), undefined) }));
      return [...added, ...modified, ...removed];
    },
  };
};

// Every read revives timestamps BEFORE the constraints run, so orderBy sees a
// real Timestamp and sorts by its millisecond value rather than by object
// identity.
const readDoc = (ref) => hydrate(ref.db.store.getDoc(ref.path));

const runQuery = (ref) =>
  applyConstraints(
    ref.db.store.listDocs(collectionPathOf(ref)).map((r) => ({ ...r, data: hydrate(r.data) })),
    ref.constraints || []
  );

// ── Reads ────────────────────────────────────
export async function getDoc(ref) {
  assertRef(ref);
  return docSnapshot(ref, readDoc(ref));
}

export async function getDocs(ref) {
  assertRef(ref);
  return querySnapshot(ref, runQuery(ref));
}

// ── Writes ───────────────────────────────────
export async function setDoc(ref, data, options = {}) {
  assertRef(ref);
  const base = options.merge ? ref.db.store.getDoc(ref.path) : undefined;
  ref.db.store.setDoc(ref.path, resolveWrite(base, data));
}

export async function addDoc(colRef, data) {
  assertRef(colRef);
  const ref = doc(colRef);
  await setDoc(ref, data);
  return ref;
}

export async function updateDoc(ref, patch) {
  assertRef(ref);
  const current = ref.db.store.getDoc(ref.path);
  // expandPaths only here: dotted keys are nested field paths for update(),
  // and plain keys for set(). See the note on resolveWrite.
  ref.db.store.setDoc(ref.path, resolveWrite(current, patch, { expandPaths: true }));
}

export async function deleteDoc(ref) {
  assertRef(ref);
  ref.db.store.deleteDoc(ref.path);
}

// ── Listeners ────────────────────────────────
export function onSnapshot(ref, ...rest) {
  assertRef(ref);
  // The real SDK's overloads, all of which reach this shim:
  //   onSnapshot(ref, cb) · onSnapshot(ref, cb, errCb)
  //   onSnapshot(ref, { next, error })
  //   onSnapshot(ref, options, cb, errCb)   ← the one that was missing
  //
  // ⚠️ THE OPTIONS OVERLOAD CRASHED THE WHOLE APP. `listenToFacilityMeta`
  // subscribes with `{ includeMetadataChanges: true }` as the SECOND argument,
  // which this read as the callback: `next` came out undefined and the first
  // emit threw "n is not a function" — killing every screen that mounts the
  // live-connection canary (the timeline rail) the moment the sandbox opened
  // it. A shim has to accept every signature its caller is allowed to use, not
  // the ones it happens to have seen.
  //
  // Options are ACCEPTED AND IGNORED: `includeMetadataChanges` asks to be
  // notified when metadata changes, and local metadata never changes.
  const isObserver = (v) => v && typeof v === "object" && typeof v.next === "function";
  const isOptions = (v) => v && typeof v === "object" && !isObserver(v);
  const args = isOptions(rest[0]) ? rest.slice(1) : rest;
  const first = args[0];
  const next = typeof first === "function" ? first : first?.next;
  const collectionPath = collectionPathOf(ref);

  if (ref.kind === "document") {
    const emit = () => next(docSnapshot(ref, readDoc(ref)));
    emit();
    return ref.db.store.subscribe(collectionPath, emit);
  }

  let previousIds = null;
  const emit = () => {
    const rows = runQuery(ref);
    next(querySnapshot(ref, rows, previousIds));
    previousIds = new Set(rows.map((r) => r.id));
  };
  emit();
  return ref.db.store.subscribe(collectionPath, emit);
}

// ── Batches and transactions ─────────────────
// One notification per touched collection AFTER the whole group lands, so a
// batch does not make listeners see the half-written intermediate state.
//
// The suspend/resume bracket is try/finally on purpose: a throw between them
// would leave the store suspended for the rest of the session, and a store
// that has stopped notifying produces a board that is quietly stale rather
// than an error anyone can see.
const applyGrouped = (db, ops) => {
  const touched = new Set();
  db.store.suspend();
  try {
    for (const op of ops) {
      touched.add(op.ref.path.split("/")[0]);
      if (op.type === "delete") db.store.deleteDoc(op.ref.path);
      else {
        const base = op.type === "update" || op.merge
          ? db.store.getDoc(op.ref.path)
          : undefined;
        // batch.update()/tx.update() expand dotted field paths exactly as
        // updateDoc does; batch.set()/tx.set() do not.
        db.store.setDoc(
          op.ref.path,
          resolveWrite(base, op.data, { expandPaths: op.type === "update" })
        );
      }
    }
  } finally {
    db.store.resume();
  }
  return touched;
};

export function writeBatch(db) {
  assertDb(db);
  const ops = [];
  // Queueing stores the RAW patch. Sentinels are resolved once, in
  // applyGrouped, against the value the document has at commit time.
  return {
    set: (ref, data, options = {}) => { assertRef(ref); ops.push({ type: "set", ref, data, merge: !!options.merge }); },
    update: (ref, data) => { assertRef(ref); ops.push({ type: "update", ref, data }); },
    delete: (ref) => { assertRef(ref); ops.push({ type: "delete", ref }); },
    commit: async () => { applyGrouped(db, ops); },
  };
}

export async function runTransaction(db, updateFn) {
  assertDb(db);
  const ops = [];
  const tx = {
    get: async (ref) => { assertRef(ref); return docSnapshot(ref, readDoc(ref)); },
    set: (ref, data, options = {}) => { assertRef(ref); ops.push({ type: "set", ref, data, merge: !!options.merge }); },
    update: (ref, data) => { assertRef(ref); ops.push({ type: "update", ref, data }); },
    delete: (ref) => { assertRef(ref); ops.push({ type: "delete", ref }); },
  };
  // A throw discards every queued write — nothing is applied until the body
  // completes. There is no contention in a single-user local store, so no retry.
  const result = await updateFn(tx);
  applyGrouped(db, ops);
  return result;
}
